import React, { useState } from "react";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useTranslation } from "react-i18next";

import ButtonBlue from "../../../../shared/general/ButtonBlue";
import utils from "../../../../../../utils";

import { useDispatch, useSelector } from "react-redux";
import { addLeaveEmployees } from "../../../../../../actions/leave";
import { getLeaveRequestAllYear } from "../../../../../../actions/employee";
import { useParams } from "react-router-dom";

const StyledRoot = styled("div")({
  width: 350,
  padding: 24,
  "& .wrap-footer-btn": {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 16,
  },
});

const StyledRow = styled("div")({
  marginBottom: 16,
});

const DialogConfirmLeave = (props) => {
  const {
    handleClose,
    data,
    handleChangeAlertType,
    handleOpenAlert,
    openConfirm,
    leaveRoundConfig,
    fetchingLeaveEmployeesList,
    fetchingLeaveAvailable,
  } = props;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const today = dayjs().toDate();
  dayjs.extend(isBetween);
  dayjs.extend(isSameOrAfter);
  const { result: userProfile } = useSelector((state) => state.userProfile);
  const { result: employeeProfile } = useSelector((state) => state.employeeProfile);
  const { result: leaveAvailable } = useSelector((state) => state.leaveAvailable);
  const [disabledBtn, setDisabledBtn] = useState(false);

  const handleSubmit = async () => {
    setDisabledBtn(true);
    const formData = {
      idLeaveType: data.idLeaveType,
      description: data.description,
      start: dayjs(`${dayjs(data.startDate).format("YYYY-MM-DD")} ${data.startDateHour}:${data.startDateMinute}:00`).format("YYYY-MM-DD HH:mm:ss"),
      end: dayjs(`${dayjs(data.endDate).format("YYYY-MM-DD")} ${data.endDateHour}:${data.endDateMinute}:00`).format("YYYY-MM-DD HH:mm:ss"),
      idEmp: employeeProfile.idEmp,
      used: data.used,
      quota: data.quota,
      balance: data.balance,
      remaining: data.remaining,
      idManagerEmployee: employeeProfile.idManagerLV1,
      managerLV1Email: employeeProfile.managerLV1Email,
      approveDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      isApprove: true,
      fillInCreate: userProfile.idUsers,
      fillInApproveLV1: userProfile.idUsers,
      isFullDay: data.leaveDayType === 'fullday'? 1: 0,
      isActive: 1,
      leaveName: data && leaveAvailable && renderLeaveType(),
    };

    const postData = new FormData();

    Object.keys(formData).map((k) => {
      postData.append(k, JSON.stringify(formData[k]));
    });

    if (data.file) {
      postData.append("file", data.file);
    }

    const result = await dispatch(addLeaveEmployees(postData));
    if (result) {
      if (result.status !== 200) {
        handleChangeAlertType(result.data.message || "error");
        handleOpenAlert(true);
      } else {
        handleChangeAlertType("success");
        handleOpenAlert(true);
        fetchingLeaveEmployeesList(leaveRoundConfig.start, leaveRoundConfig.end);
        fetchingLeaveAvailable();
        openConfirm(false);
        handleClose();
      }
    } else {
      openConfirm(false);
      handleOpenAlert(true);
      handleChangeAlertType("error");
    }
  };

  const renderLeaveType = () => {
    const found = leaveAvailable && leaveAvailable.find(
      (item) => item.idLeaveType === data.idLeaveType
    );
    return found ? found.name : "-";
  };

  return (
    <StyledRoot>
      <Typography variant="h6">{t("ConfirmLeave")}</Typography>
      <div>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("LeaveType")}
          </Typography>
          <Typography variant="h6">
            {data && leaveAvailable && renderLeaveType()}
          </Typography>
        </StyledRow>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("StartDate")}
          </Typography>
          <Typography variant="h6">
            {data.leaveDayType === "fullday"
              ? `${dayjs(`${dayjs(data.startDate).format("YYYY-MM-DD")} ${data.startDateHour}:${data.startDateMinute}:00`).format("DD MMMM YYYY")} (เต็มวัน)`
              : `${dayjs(`${dayjs(data.startDate).format("YYYY-MM-DD")} ${data.startDateHour}:${data.startDateMinute}:00`).format("DD MMMM YYYY HH:mm")}`
            }
          </Typography>
        </StyledRow>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("EndDate")}
          </Typography>
          <Typography variant="h6">
            {data.leaveDayType === "fullday"
              ? `${dayjs(`${dayjs(data.endDate).format("YYYY-MM-DD")} ${data.endDateHour}:${data.endDateMinute}:00`).format("DD MMMM YYYY")} (เต็มวัน)`
              : `${dayjs(`${dayjs(data.endDate).format("YYYY-MM-DD")} ${data.endDateHour}:${data.endDateMinute}:00`).format("DD MMMM YYYY HH:mm")}`
            }
          </Typography>
        </StyledRow>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("NumberLeaveDays")}
          </Typography>
          <Typography variant="h6">
            {Math.round(data.used * 100)/100} วัน
          </Typography>
        </StyledRow>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("RemainingLeaveRights")}
          </Typography>
          <Typography variant="h6">
            {data.balance === null? "ไม่จำกัด": `${Math.round(data.balance * 100)/100} วัน`}
          </Typography>
        </StyledRow>
        <StyledRow>
          <Typography variant="body2" color="text.secondary">
            {t("Reason")}
          </Typography>
          {data.description ? (
            <Typography variant="h6">{data.description}</Typography>
          ) : (
            <Typography
              variant="h6"
              color="text.secondary"
              style={{ fontStyle: "oblique" }}
            >
              {t("NotSpecified")}
            </Typography>
          )}
        </StyledRow>
      </div>
      <div className="wrap-footer-btn">
        <ButtonBlue
          size=""
          variant={"text"}
          color="secondary"
          onClick={handleClose}
        >
          {t("Cancel")}
        </ButtonBlue>
        <ButtonBlue
          disabled={disabledBtn}
          onClick={handleSubmit}
          autoFocus
          variant={"contained"}
        >
          {t("ConfirmLeave")}
        </ButtonBlue>
      </div>
    </StyledRoot>
  );
};

export default DialogConfirmLeave;
